import React, { useEffect } from 'react';
import { Box } from 'native-base';

import { useUpdateMember, useSpringBigStore, getMember, useI18n } from '@springbig/shared';

import { accountForms, GenForm, GenFormProps } from './components/AccountInformationForm';
import Alert from '~/shared/Alert';
import { useGeneralStyles, useMenuStyles } from '~/shared/styles';
import { useToast } from '~/hooks/toast';
import { useAppNavigate } from '..';
import Button from '~/shared/Button';
import { useAuthorization } from '~/hooks/auth';
import Loading from '~/shared/Loading';
import Section from '~/shared/Section';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useController, useForm } from 'react-hook-form';

const emailSchema = yup.object().shape({
    email: yup
        .string()
        .required('email is required')
        .matches(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,63}$/i, 'invalid email format')
        .trim(),
});

const zipSchema = yup.object().shape({
    zip: yup
        .string()
        .required('zip code is required')
        .trim()
        .matches(
            /^(\d{5}(?:[-\s]\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$/,
            'invalid zip code format'
        ),
});


const AccountInformationScreen = () => {


    const { member, setMember, walletId } = useSpringBigStore();
    const { t } = useI18n();
    const [formData, setFormData] = React.useState<GenFormProps['formKeys'][]>();
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);

    const { mutate: updateMember, isLoading, data: memberUpdated } = useUpdateMember();

    const { navigate, params } = useAppNavigate();
    const { successToast } = useToast();
    const gStyle = useGeneralStyles();

    const { isLoadingAuth } = useAuthorization();
    const {
        background
    } = useMenuStyles();
    const { control: controlEmail, handleSubmit: handleSubmitEmail, formState: { errors: errorsEmail }, setValue: setEmailValue } = useForm({
        resolver: yupResolver(emailSchema)
    });

    const { field: emailField } = useController({
        control: controlEmail,
        name: 'email',
    });

    const { control: controlZip, handleSubmit: handleSubmitZip, formState: { errors: errorsZip }, setValue: setZipValue } = useForm({
        resolver: yupResolver(zipSchema)
    });

    const { field: zipField } = useController({
        control: controlZip,
        name: 'zip',
        defaultValue: undefined
    });

    const formSchema = {
        email: {
            field: emailField,
            validatorError: errorsEmail?.email?.message,
            validator: handleSubmitEmail
        },
        zip: {
            field: zipField,
            validatorError: errorsZip?.zip?.message,
            validator: handleSubmitZip
        }
    };

    useEffect(() => {

        if (member) {
            const data_: GenFormProps['formKeys'][] = accountForms.map(el => {

                return {
                    ...el,
                    value: member[el.name] || undefined
                };
            });

            setFormData(data_);
            if (member.email) {

                setEmailValue('email', member.email);
            }

            if (member.zip) {

                setZipValue('zip', member.zip);
            }
        }
    }, [member]);

    useEffect(() => {

        if (memberUpdated) {
            handleMember(walletId || '');
            successToast('Member updated successfully');
        }

    }, [memberUpdated]);

    const handleMember = async (id: string) => {

        const member_ = id && await getMember({ walletId: id });

        if (member_) {
            setMember(member_);
        }

    };

    const handleSubmitValues = (values: GenFormProps['formKeys']) => {

        updateMember({
            member: {
                [values.name]: values.value
            }
        });
    };

    const handleDeleteAccount = () => {

        navigate(`w/${params.wallet_id}/profile/menu/account/remove`);
    };

    if (isLoadingAuth) {
        return <Loading isLoading={isLoadingAuth} />;
    }

    return (
        <Section pb={20} mb={4} backgroundColor={background}>
            {
                formData && formData.map((el) => {

                    if (formSchema[el.name]) {
                        return (
                            <Box
                                px={4}
                                key={el.name}
                                bg={gStyle.section_background}
                            >
                                <GenForm
                                    field={formSchema[el.name].field}
                                    validatorError={formSchema[el.name].validatorError}
                                    validator={formSchema[el.name].validator}
                                    handleSubmitValues={handleSubmitValues}
                                    isLoading={isLoading}
                                    formKeys={el}
                                />
                            </Box>
                        );
                    }

                    return (
                        <Box
                            px={4}
                            key={el.name}
                            bg={gStyle.section_background}
                        >
                            <GenForm
                                handleSubmitValues={handleSubmitValues}
                                isLoading={isLoading}
                                formKeys={el}
                            />
                        </Box>
                    );
                })
            }
            <Box alignItems={'center'} my={2} >
                <Button
                    bg={gStyle.button_danger_background}
                    _text={{
                        color: gStyle.button_danger_text
                    }}
                    h={12}
                    w={32}
                    onPress={(() => {

                        setShowDeleteModal(true);
                    })}
                >
                    {
                        t('accountInformation.delete_account')
                    }
                </Button>
            </Box>
            {
                showDeleteModal && (
                    <Alert
                        setAcceptAlert={() => {

                            setShowDeleteModal(false);
                            handleDeleteAccount();
                        }}
                        setCancelAlert={() => setShowDeleteModal(false)}
                        showAlert={showDeleteModal}
                        message={t('accountInformation.delete_account_message')}
                        title={t('accountInformation.delete_account_title')}
                    />
                )
            }
        </Section>
    );
};

export default AccountInformationScreen;
